import {
  computed,
  defineStore,
  ref,
  useCategories,
  getHashesForArea,
  reactive,
  watch,
  useSpots,
  usePlaces,
  useCreators,
} from "#imports";
import { useDebounce, useDebounceFn } from "@vueuse/core";
import { uniq } from "lodash-es";
import { createLogger } from "~/utils/logger";

export const minZoomVisibleSpots = 12;
export const minZoomVisiblePlaces = 10;
const minZoomLoadSpots = minZoomVisibleSpots - 2;
const minZoomLoadPlaces = minZoomVisiblePlaces - 2;

export const useExploreX = defineStore("explore", () => {
  const logger = createLogger("stores/explore");
  const categoriesStore = useCategories();
  const spotsStore = useSpots();
  const placesStore = usePlaces();
  const creatorStore = useCreators();

  // these are the controllers for the store

  type LatLng = { lat: number; lng: number };
  /** @private */
  const rawCurrentLatLng = ref<{ min: LatLng; max: LatLng }>();
  /** @private */
  const currentLatLng = useDebounce(rawCurrentLatLng, 100, { maxWait: 100 });
  /** @private */
  const currentZoom = ref<number>(0);

  /** Set the min/max lat/lng of the currently viewable map area */
  const setLatLng = (payload: { min: LatLng; max: LatLng; zoom: number }) => {
    currentZoom.value = payload.zoom;
    rawCurrentLatLng.value = { min: payload.min, max: payload.max };
  };

  const areSpotsVisible = computed(
    () => currentZoom.value >= minZoomVisibleSpots,
  );
  const arePlacesVisible = computed(
    () => !areSpotsVisible.value && currentZoom.value >= minZoomVisiblePlaces,
  );
  const areRegionsVisible = computed(
    () => !areSpotsVisible.value && !arePlacesVisible.value,
  );

  /** @private */
  const currentCategories = reactive<string[]>([]);
  /** Set the category filters for the map, use empty array to unset */
  const setCategories = (payload: string[]) => {
    currentCategories.length = 0;
    Object.assign(currentCategories, payload);
  };

  /** @private calculate hashes from current lat/lng */
  const currentGeohashes = computed(() => {
    if (!currentLatLng.value?.min || !currentLatLng.value?.max) {
      return [];
    }
    return getHashesForArea(currentLatLng.value.min, currentLatLng.value.max);
  });

  const currentCreator = ref<string | null>(null);
  const setCreator = (payload: string | null) => {
    currentCreator.value = payload;
  };

  /** @private */
  const areSpotsLoading = ref<boolean>(false);
  const loadedPlaceGeohashes = ref<number[]>([]);
  const loadedSpotGeohashes = ref<number[]>([]);
  /** @private populate geohash cache spots and places within currentGeohashes */
  const loadGeohashData = async () => {
    if (areSpotsLoading.value) return; // run only once
    areSpotsLoading.value = true;

    if (currentZoom.value >= minZoomLoadPlaces) {
      loadedPlaceGeohashes.value = uniq(
        loadedPlaceGeohashes.value.concat(currentGeohashes.value),
      );
      await placesStore.fetchByGeohashes(currentGeohashes.value);
    }
    if (currentZoom.value >= minZoomLoadSpots) {
      loadedSpotGeohashes.value = uniq(
        loadedSpotGeohashes.value.concat(currentGeohashes.value),
      );
      await spotsStore.fetchByGeohashes(currentGeohashes.value);
      const creatorIds = uniq(
        spotsStore
          .spotByGeohashes(currentGeohashes.value)
          .value.map((spot) => spot.creatorId),
      );
      await creatorStore.fetchByIds(creatorIds);
    }

    areSpotsLoading.value = false;
  };

  watch(
    currentGeohashes,
    useDebounceFn(loadGeohashData, 100, { maxWait: 250 }),
  );

  const reset = () => {
    // reset the loaded geohashes
    loadedPlaceGeohashes.value = [];
    loadedSpotGeohashes.value = [];
    setCategories([]);
  };

  const pointWithinArea = (
    point: LatLng,
    area: { min: LatLng; max: LatLng },
  ) => {
    // I'm not sure this will cope near lat 0
    return (
      point.lat >= area.min.lat &&
      point.lat <= area.max.lat &&
      point.lng >= area.min.lng &&
      point.lng <= area.max.lng
    );
  };

  /** return spot data loaded, with isVisible flag based on geohashes and categories */
  const exploreSpots = computed(() => {
    // difficult to get the type info here
    const stopTimer = logger.time("exploreSpots");
    const ret = Object.values(
      spotsStore.spotByGeohashes(loadedSpotGeohashes.value).value,
    ).map((spot) => {
      const isWithinBounds =
        areSpotsVisible.value &&
        pointWithinArea({ lat: spot.lat, lng: spot.lon }, currentLatLng.value!);

      return {
        ...spot,
        category: categoriesStore.getById(spot.category_id!).value,

        // this controls if the spot is currently visible by zoom and within the viewable area
        isWithinBounds,

        // this controls if the spot is within the current filter
        // and has an image and creator has a mappr_handle
        isVisible:
          isWithinBounds &&
          spot.cover_image_url &&
          creatorStore.getById(spot.creatorId).value?.mappr_handle &&
          (currentCategories.length === 0 ||
            currentCategories.includes(spot.category_id!)) &&
          (currentCreator.value === null ||
            currentCreator.value === spot.creatorId),
      };
    });

    stopTimer(`points: ${ret.length}`);

    return ret;
  });

  // spots within the current bounds
  const spotsWithinBounds = computed(() =>
    exploreSpots.value.filter((spot) => spot.isWithinBounds),
  );

  // spots that fit filters and are within bounds
  const visibleSpots = computed(() =>
    spotsWithinBounds.value.filter((spot) => spot.isVisible),
  );

  /** return unique categories for currently visible spots */
  const visibleCategories = computed(() => {
    if (!areSpotsVisible.value) {
      return categoriesStore.categories!;
    }

    const catIds = uniq(
      spotsWithinBounds.value
        .map((spot) => spot.category_id)
        .filter((catId) => typeof catId === "string"),
    );

    const ret = catIds.map((catId) => categoriesStore.getById(catId).value!);

    if (ret.length === 0) {
      // nothing visible? return all categories
      return categoriesStore.categories!;
    } else {
      return ret;
    }
  });

  /** return unique creators for currently visible spots */
  const visibleCreators = computed(() => {
    if (!areSpotsVisible.value) {
      return [];
    }

    const creatorIds = uniq(
      spotsWithinBounds.value.map((spot) => spot.creatorId),
    );
    if (creatorIds.length > 0) {
      return creatorStore.getByIds(creatorIds).value;
    }
    return [];
  });

  /** Return regions and places, with isVisible flag */
  const explorePlaces = computed(() => {
    const regions = placesStore.regions!.map((region) => ({
      ...region,
      isVisible: areRegionsVisible.value,
    }));

    const places = Object.values(
      placesStore.placesByGeohashes(loadedPlaceGeohashes.value).value,
    ).map((place) => ({
      ...place,
      isVisible:
        arePlacesVisible.value && pointWithinArea(place, currentLatLng.value!),
    }));

    const ret = places.concat(regions); // glue places and regions together
    return ret;
  });

  return {
    rawCurrentLatLng,
    currentLatLng,
    currentZoom,
    currentCategories,
    currentGeohashes,
    currentCreator,
    areSpotsLoading,

    loadedPlaceGeohashes,
    loadedSpotGeohashes,

    arePlacesVisible,
    areRegionsVisible,
    areSpotsVisible,

    // and we need these refs and functions for the FE
    reset,
    setLatLng,
    setCategories,
    setCreator,
    exploreSpots,

    visibleSpots,
    visibleCategories,
    visibleCreators,
    explorePlaces,
  };
});
